import { defineDocument } from '../../schema';
export const gameTooManyCooksDocument = defineDocument({
    id: 'game:too-many-cooks',
    kind: 'game',
    slug: 'too-many-cooks',
    route: '/discover/games/too-many-cooks',
    seo: {
        title: 'Discover Too Many Cooks',
        description: 'A culinary party game where survey answers become ingredients. Create dishes, earn points when yours are used, and win votes for the best dish.',
        imageAsset: 'coverTooManyCooks',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Too Many Cooks',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Players create dishes from shared ingredients and vote on the best. Points earned when your ingredients get used or your dishes win votes.',
            },
        ],
        imageAsset: 'coverTooManyCooks',
        imageAlt: 'Too Many Cooks game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Step into a chaotic kitchen where every chef thinks they're the star.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "You'll create three courses. The Chef Survey stocks the pantry with the ingredients you need to craft each of your world-class dishes.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Score points when your ingredients are used and even more when your dishes win votes. Only the most elite culinary masters will rise to the top in a kitchen with Too Many Cooks.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '3-10 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free-For-All',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Free Input, Selection, Voting',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~17 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'January 6, 2026',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6944abab5d2f2d3fde971460',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'All players (chefs) answer a set of five survey questions. Answers become ingredients for chefs to cook with.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Each chef selects three ingredients to make the name of a dish. The same ingredient can be used multiple times. A chef can use their own ingredients.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Chefs vote for the dish they think is best (but not their own).',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'An image of the best dish is revealed.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat for three rounds: appetizer, main dish, and dessert.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Earning points:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: '25 points every time one of your ingredients is used in a dish.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: '100 points for each vote your dish receives.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The player with the highest score at the end of the game wins.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Chef survey answers',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Best dish names',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Chef survey questions',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Best dish image',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: low',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: streamers, those who appreciate absurd humor, creatives',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/too-many-cooks-hits-the-kitchen',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Too Many Cooks blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6944abab5d2f2d3fde971460',
                },
            ],
        },
    ],
});
//# sourceMappingURL=too-many-cooks.js.map